import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAdminAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, ArrowLeft } from "lucide-react";
import { LOGO_URL } from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAdminAuth();
  const [email, setEmail] = useState("admin@beddinglounge.pk");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-cream" data-testid="admin-login-page">
      {/* Side panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-cream-warm relative overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/1b235423-2870-43ca-b9e6-4a44f088b6f4/images/5d96ae127f122e340202493ff75cd939ea9d1aaf56d7d13eb16d26279e7b7519.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-ink/70 via-ink/30 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-cream">
          <Link to="/" className="inline-flex items-center gap-2 text-cream/80 hover:text-cream text-xs uppercase tracking-widest" data-testid="login-back-store">
            <ArrowLeft size={14} /> Back to Store
          </Link>
          <div>
            <div className="label-eyebrow !text-cream/80 mb-3">Admin Suite</div>
            <h1 className="font-display text-5xl tracking-tight">The brain behind the brand.</h1>
            <p className="mt-4 text-cream/85 max-w-md leading-relaxed">
              Manage products, orders, logistics, and analytics — all from a single calm dashboard.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <img src={LOGO_URL} alt="" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-display text-2xl text-ink">Bedding Lounge</div>
              <div className="label-eyebrow !text-[10px]">Admin Portal</div>
            </div>
          </div>
          <h2 className="font-display text-4xl text-ink mb-2">Sign in</h2>
          <p className="text-ink-soft text-sm mb-8">Use your admin credentials to continue.</p>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-ink-soft text-xs uppercase tracking-widest">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="admin-email"
                className="mt-1.5 bg-cream border-border-subtle"
              />
            </div>
            <div>
              <Label className="text-ink-soft text-xs uppercase tracking-widest">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password"
                className="mt-1.5 bg-cream border-border-subtle"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-sage hover:bg-sage-dark text-cream py-6 text-xs uppercase tracking-[0.22em] font-semibold transition-colors"
              data-testid="admin-login-submit"
            >
              <Lock size={14} className="mr-2" /> {submitting ? "Signing in…" : "Sign In"}
            </Button>
          </form>
          <div className="mt-6 text-xs text-ink-soft">
            Default seeded admin: <span className="font-semibold text-ink">admin@beddinglounge.pk</span>
          </div>
        </div>
      </div>
    </div>
  );
}
